import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Award, Presentation, Download, FileText, Mic, Clock, BookOpen, Calendar, ClipboardCheck, FileEdit, Upload, File as FileIcon } from 'lucide-react';
import { toast } from 'sonner';
import { uploadRecevabiliteFile, fetchRecevabilite, getFileUrl } from '../../services/api';

const TP_IEPE_DOCUMENTS = [
  {
    title: 'Livret 2 détaillé (Word)',
    description: 'Livret 2 complet à remplir pour votre dossier VAE TP IEPE',
    url: 'https://customer-assets.emergentagent.com/job_7ebc9951-ac6a-45ad-bbd9-5b722966c82a/artifacts/xj0jlhb7_livret%202%20en%20word.docx',
    icon: FileText,
    color: '#3B82F6',
    ext: 'DOCX'
  },
  {
    title: 'Trame du diaporama (PowerPoint)',
    description: 'Modèle de diaporama prêt à personnaliser pour votre oral',
    url: 'https://customer-assets.emergentagent.com/job_7ebc9951-ac6a-45ad-bbd9-5b722966c82a/artifacts/8kgierl9_modele%20diaporama_tp_iepe.pptx',
    icon: Presentation,
    color: '#F97316',
    ext: 'PPTX'
  }
];

const MonParcoursTab = ({
  enrolledProgram, selectedProgramId, allCourses,
  userProgressList, recevabiliteList, livret2Corrections,
  userProgressData, accompanimentData, currentFormula, entitlements, user,
  dragOver, setDragOver, uploading, setUploading, setRecevabiliteList
}) => {
  return (
          <div data-testid="mon-parcours-tab">
            <h2 className="heading-2" style={{ marginBottom: '0.5rem' }}>
              Mon Parcours — {enrolledProgram?.title || 'Sélectionnez un diplôme'}
            </h2>
            <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              {enrolledProgram?.fullName}
            </p>

            {/* Filtered data for selected program */}
            {(() => {
              const programCourses = allCourses.filter(c => c.programId === selectedProgramId && !c.isHeader);
              const programProgress = userProgressList.filter(p => {
                const course = allCourses.find(c => c.id === p.courseId);
                return course && course.programId === selectedProgramId;
              });
              const programRecevabilite = recevabiliteList.filter(r => r.programId === selectedProgramId);
              const programCorrections = livret2Corrections.filter(c => c.programId === selectedProgramId);
              const totalTime = programProgress.reduce((sum, p) => sum + (p.timeSpent || 0), 0);
              const totalH = Math.floor(totalTime / 3600);
              const totalM = Math.floor((totalTime % 3600) / 60);

              return (
                <>
                  {/* Summary Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
                    <div className="card accent-purple" data-testid="total-time-card">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <Clock size={22} style={{ color: 'var(--primary)' }} />
                        <span className="body-small" style={{ fontWeight: 600 }}>Temps total</span>
                      </div>
                      <p className="heading-2" style={{ margin: 0 }}>
                        {totalH > 0 ? `${totalH}h ${totalM}min` : `${totalM}min`}
                      </p>
                    </div>

                    <div className="card accent-blue" data-testid="courses-accessed-card">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <BookOpen size={22} style={{ color: '#3B82F6' }} />
                        <span className="body-small" style={{ fontWeight: 600 }}>Cours consultés</span>
                      </div>
                      <p className="heading-2" style={{ margin: 0 }}>
                        {programProgress.filter(p => (p.timeSpent || 0) > 0).length} / {programCourses.length}
                      </p>
                    </div>

                    <div className="card" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1px solid #bbf7d0' }} data-testid="courses-completed-card">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <CheckCircle size={22} style={{ color: '#16a34a' }} />
                        <span className="body-small" style={{ fontWeight: 600 }}>Avancement global</span>
                      </div>
                      <p className="heading-2" style={{ margin: 0 }}>
                        {(() => {
                          let totalPct = 0;
                          let count = 0;
                          programCourses.forEach(c => {
                            if (c.type === 'presentation' && c.presentationType) {
                              const prog = userProgressData[c.presentationType];
                              if (prog && prog.totalSlides > 0) {
                                totalPct += Math.min(100, Math.round((prog.viewedSlides.length / prog.totalSlides) * 100));
                                count++;
                              }
                            } else if (c.type === 'quiz' && c.presentationType) {
                              const prog = userProgressData[c.presentationType];
                              if (prog && prog.quizCompleted) {
                                totalPct += 100;
                                count++;
                              }
                            }
                          });
                          const presentationCount = programCourses.filter(c => c.type === 'presentation' || c.type === 'quiz').length;
                          return presentationCount > 0 ? `${Math.round(totalPct / presentationCount)}%` : '0%';
                        })()}
                      </p>
                    </div>
                  </div>


                  {/* TP IEPE Documents Section */}
                  {selectedProgramId === 'tp' && (
                    <div className="card" style={{ marginBottom: '2.5rem', padding: '1.5rem' }} data-testid="tp-iepe-documents">
                      <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Download size={20} /> Documents à télécharger
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                        {TP_IEPE_DOCUMENTS.map((doc, idx) => (
                          <a
                            key={idx}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`tp-document-${idx}`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '1rem',
                              padding: '1.25rem', borderRadius: '12px',
                              background: `${doc.color}08`, border: `1px solid ${doc.color}25`,
                              textDecoration: 'none', color: 'inherit',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${doc.color}20`; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                          >
                            <div style={{
                              width: '48px', height: '48px', borderRadius: '12px',
                              background: `${doc.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                              <doc.icon size={24} style={{ color: doc.color }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontWeight: 600, fontSize: '0.9rem', margin: '0 0 0.25rem 0' }}>{doc.title}</p>
                              <p style={{ fontSize: '0.78rem', color: '#6B7280', margin: 0 }}>{doc.description}</p>
                            </div>
                            <div style={{
                              padding: '0.25rem 0.5rem', borderRadius: '6px', fontSize: '0.65rem',
                              fontWeight: 700, color: doc.color, background: `${doc.color}15`, flexShrink: 0
                            }}>
                              {doc.ext}
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TP IEPE Specific Accompaniment Tracking */}
                  {selectedProgramId === 'tp' && accompanimentData && currentFormula && currentFormula !== 'bronze' && (
                    <div className="card" style={{ marginBottom: '2.5rem', padding: '1.5rem' }} data-testid="tp-accompaniment-tracking">
                      <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Award size={20} /> Suivi de l'accompagnement — TP IEPE
                      </h3>

                      {/* Corrections Livret 2 (3 blocs) */}
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#9b59b6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FileText size={16} /> Corrections du Livret 2 <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({(accompanimentData.corrections || []).filter(c => c.status === 'corrige').length}/{entitlements.corrections === 999 ? '∞' : entitlements.corrections})</span>
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
                          {(accompanimentData.corrections || []).slice(0, entitlements.corrections === 999 ? 99 : entitlements.corrections).map((c, idx) => {
                            const sc = { 'corrige': { bg: '#f0fdf4', border: '#bbf7d0', color: '#059669', label: 'Corrigé', icon: true }, 'en_cours': { bg: '#FFF7ED', border: '#fed7aa', color: '#D97706', label: 'En cours' }, 'depose': { bg: '#EFF6FF', border: '#bfdbfe', color: '#2563EB', label: 'Déposé' }, 'a_planifier': { bg: '#f8fafc', border: '#e2e8f0', color: '#94a3b8', label: 'Non déposé' } }[c.status] || { bg: '#f8fafc', border: '#e2e8f0', color: '#94a3b8', label: 'Non déposé' };
                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', background: sc.bg, border: `1px solid ${sc.border}` }} data-testid={`tp-correction-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: sc.icon ? '#059669' : (c.status !== 'a_planifier' ? sc.color : '#e2e8f0'), color: c.status !== 'a_planifier' ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                    {sc.icon ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{c.label}</span>
                                </div>
                                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '1rem', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{sc.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>


                      {/* Appels Explicatifs */}
                      {entitlements.appels > 0 && accompanimentData.appels && accompanimentData.appels.length > 0 && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Clock size={16} /> Appels explicatifs <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.appels.filter(a => a.date).length}/{entitlements.appels === 999 ? '∞' : entitlements.appels})</span>
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.appels.slice(0, entitlements.appels === 999 ? 99 : entitlements.appels).map((a, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', background: a.date ? 'linear-gradient(135deg, #ECFDF5, #fff)' : '#f8fafc', border: `1px solid ${a.date ? '#a7f3d0' : '#e2e8f0'}` }} data-testid={`tp-appel-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: a.date ? '#10B981' : '#e2e8f0', color: a.date ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                    {a.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{a.label}</span>
                                </div>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: a.date ? '#10B981' : '#94a3b8' }}>{a.date ? new Date(a.date).toLocaleDateString('fr-FR') : 'À planifier'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* RDV Personnalisation Diaporama */}
                      {entitlements.rdvDiaporama > 0 && accompanimentData.rdv_diaporama && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#F97316', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Presentation size={16} /> RDV Personnalisation diaporama <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.rdv_diaporama.filter(r => r.date).length}/{entitlements.rdvDiaporama})</span>
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.rdv_diaporama.slice(0, entitlements.rdvDiaporama).map((r, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', background: r.date ? 'linear-gradient(135deg, #FFF7ED, #fff)' : '#f8fafc', border: `1px solid ${r.date ? '#fed7aa' : '#e2e8f0'}` }} data-testid={`tp-rdv-diaporama-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: r.date ? '#F97316' : '#e2e8f0', color: r.date ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                    {r.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{r.label}</span>
                                </div>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: r.date ? '#F97316' : '#94a3b8' }}>{r.date ? new Date(r.date).toLocaleDateString('fr-FR') : 'À planifier'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Entretiens Techniques Blancs */}
                      {entitlements.entretienTechnique > 0 && accompanimentData.entretiens_techniques && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#8B5CF6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Mic size={16} /> Entretiens techniques blancs <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.entretiens_techniques.filter(e => e.date).length}/{entitlements.entretienTechnique})</span>
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.entretiens_techniques.slice(0, entitlements.entretienTechnique).map((e, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', background: e.date ? 'linear-gradient(135deg, #F5F3FF, #fff)' : '#f8fafc', border: `1px solid ${e.date ? '#c4b5fd' : '#e2e8f0'}` }} data-testid={`tp-technique-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: e.date ? '#8B5CF6' : '#e2e8f0', color: e.date ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                    {e.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{e.label}</span>
                                </div>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: e.date ? '#8B5CF6' : '#94a3b8' }}>{e.date ? new Date(e.date).toLocaleDateString('fr-FR') : 'À planifier'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Entretiens Diaporama Blancs */}
                      {entitlements.entretienDiaporama > 0 && accompanimentData.entretiens_diaporama && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#06B6D4', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Presentation size={16} /> Entretiens diaporama blancs <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.entretiens_diaporama.filter(e => e.date).length}/{entitlements.entretienDiaporama})</span>
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.entretiens_diaporama.slice(0, entitlements.entretienDiaporama).map((e, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', background: e.date ? 'linear-gradient(135deg, #ECFEFF, #fff)' : '#f8fafc', border: `1px solid ${e.date ? '#a5f3fc' : '#e2e8f0'}` }} data-testid={`tp-diaporama-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: e.date ? '#06B6D4' : '#e2e8f0', color: e.date ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                    {e.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{e.label}</span>
                                </div>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: e.date ? '#06B6D4' : '#94a3b8' }}>{e.date ? new Date(e.date).toLocaleDateString('fr-FR') : 'À planifier'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Entretiens Finaux Blancs */}
                      {entitlements.entretienFinal > 0 && accompanimentData.entretiens_finaux && (
                        <div>
                          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Award size={16} /> Entretiens finaux blancs <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.entretiens_finaux.filter(e => e.date).length}/{entitlements.entretienFinal})</span>
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.entretiens_finaux.slice(0, entitlements.entretienFinal).map((e, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', background: e.date ? 'linear-gradient(135deg, #ECFDF5, #fff)' : '#f8fafc', border: `1px solid ${e.date ? '#a7f3d0' : '#e2e8f0'}` }} data-testid={`tp-final-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: e.date ? '#10B981' : '#e2e8f0', color: e.date ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                    {e.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{e.label}</span>
                                </div>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: e.date ? '#10B981' : '#94a3b8' }}>{e.date ? new Date(e.date).toLocaleDateString('fr-FR') : 'À planifier'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Accompaniment Tracking Section (non-TP programs) */}
                  {selectedProgramId !== 'tp' && accompanimentData && currentFormula && currentFormula !== 'bronze' && (
                    <div className="card" style={{ marginBottom: '2.5rem', padding: '1.5rem' }} data-testid="accompaniment-tracking">
                      <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={20} /> Suivi de l'accompagnement — {enrolledProgram?.title}
                      </h3>

                      {/* Corrections Livret 2 */}
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#9b59b6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FileEdit size={16} /> Corrections Livret 2 <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.corrections?.filter(c => c.status === 'corrige').length || 0}/{accompanimentData.corrections?.length || (selectedProgramId === 'deeje' ? 6 : 5)})</span>
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
                          {(accompanimentData.corrections || []).map((c, idx) => {
                            const statusConfig = {
                              'corrige': { bg: '#f0fdf4', border: '#bbf7d0', color: '#059669', iconBg: '#059669', label: 'Corrigé', icon: true },
                              'en_cours': { bg: '#FFF7ED', border: '#fed7aa', color: '#D97706', iconBg: '#D97706', label: 'En cours', icon: false },
                              'depose': { bg: '#EFF6FF', border: '#bfdbfe', color: '#2563EB', iconBg: '#2563EB', label: 'Déposé', icon: false },
                              'a_planifier': { bg: '#f8fafc', border: '#e2e8f0', color: '#94a3b8', iconBg: '#e2e8f0', label: 'Non déposé', icon: false }
                            };
                            const s = statusConfig[c.status] || statusConfig['a_planifier'];
                            return (
                            <div key={idx} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '0.75rem 1rem', borderRadius: '10px',
                              background: s.bg,
                              border: `1px solid ${s.border}`
                            }} data-testid={`correction-slot-${idx+1}`}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{
                                  width: '28px', height: '28px', borderRadius: '50%',
                                  background: s.icon ? s.iconBg : (c.status !== 'a_planifier' ? s.iconBg : '#e2e8f0'),
                                  color: c.status !== 'a_planifier' ? 'white' : '#94a3b8',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.7rem', fontWeight: 700, flexShrink: 0
                                }}>
                                  {s.icon ? <CheckCircle size={14} /> : idx + 1}
                                </div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{c.label}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{
                                  fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '1rem',
                                  background: s.bg, color: s.color, border: `1px solid ${s.border}`
                                }}>
                                  {s.label}
                                </span>
                                {c.date && <span style={{ fontSize: '0.72rem', color: '#6B7280' }}>{new Date(c.date).toLocaleDateString('fr-FR')}</span>}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Présentation personnelle (Silver/Gold) - avant les oraux */}
                      {(currentFormula === 'gold' || currentFormula === 'silver') && accompanimentData.presentations_perso && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#8B5CF6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Presentation size={16} /> Présentation personnelle <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({accompanimentData.presentations_perso.filter(p => p.date).length}/{entitlements.presentationPerso})</span>
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.presentations_perso.slice(0, entitlements.presentationPerso).map((pp, idx) => (
                              <div key={idx} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.75rem 1rem', borderRadius: '10px',
                                background: pp.date ? 'linear-gradient(135deg, #F5F3FF, #fff)' : '#f8fafc',
                                border: `1px solid ${pp.date ? '#c4b5fd' : '#e2e8f0'}`
                              }} data-testid={`presentation-perso-slot-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{
                                    width: '28px', height: '28px', borderRadius: '50%',
                                    background: pp.date ? '#8B5CF6' : '#e2e8f0',
                                    color: pp.date ? 'white' : '#94a3b8',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.7rem', fontWeight: 700, flexShrink: 0
                                  }}>
                                    {pp.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{pp.label}</span>
                                </div>
                                <span style={{
                                  fontSize: '0.78rem', fontWeight: 600,
                                  color: pp.date ? '#8B5CF6' : '#94a3b8'
                                }}>
                                  {pp.date ? new Date(pp.date).toLocaleDateString('fr-FR') : 'À planifier'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Oral blanc */}
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '1rem', color: '#F97316', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Award size={16} /> {entitlements.oralBlanc > 1 ? 'Oraux blancs' : 'Oral blanc'} {(currentFormula === 'gold' || currentFormula === 'silver') && <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6B7280' }}>({(accompanimentData.oral_blancs || []).filter(o => o.date).length}/{entitlements.oralBlanc})</span>}
                        </h4>
                        {(currentFormula === 'gold' || currentFormula === 'silver') && accompanimentData.oral_blancs ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                            {accompanimentData.oral_blancs.slice(0, entitlements.oralBlanc).map((ob, idx) => (
                              <div key={idx} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.75rem 1rem', borderRadius: '10px',
                                background: ob.date ? 'linear-gradient(135deg, #FFF7ED, #fff)' : '#f8fafc',
                                border: `1px solid ${ob.date ? '#fed7aa' : '#e2e8f0'}`
                              }} data-testid={`oral-blanc-slot-${idx+1}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{
                                    width: '28px', height: '28px', borderRadius: '50%',
                                    background: ob.date ? '#F97316' : '#e2e8f0',
                                    color: ob.date ? 'white' : '#94a3b8',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.7rem', fontWeight: 700, flexShrink: 0
                                  }}>
                                    {ob.date ? <CheckCircle size={14} /> : idx + 1}
                                  </div>
                                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{ob.label}</span>
                                </div>
                                <span style={{
                                  fontSize: '0.78rem', fontWeight: 600,
                                  color: ob.date ? '#F97316' : '#94a3b8'
                                }}>
                                  {ob.date ? new Date(ob.date).toLocaleDateString('fr-FR') : 'À planifier'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '1rem 1.25rem', borderRadius: '10px',
                            background: accompanimentData.oral_blanc?.date ? 'linear-gradient(135deg, #FFF7ED, #fff)' : '#f8fafc',
                            border: `1px solid ${accompanimentData.oral_blanc?.date ? '#fed7aa' : '#e2e8f0'}`,
                            maxWidth: '400px'
                          }} data-testid="oral-blanc-slot">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{
                                width: '36px', height: '36px', borderRadius: '50%',
                                background: accompanimentData.oral_blanc?.date ? '#F97316' : '#e2e8f0',
                                color: accompanimentData.oral_blanc?.date ? 'white' : '#94a3b8',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0
                              }}>
                                <Award size={18} />
                              </div>
                              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Oral blanc de préparation</span>
                            </div>
                            <span style={{
                              fontSize: '0.85rem', fontWeight: 600,
                              color: accompanimentData.oral_blanc?.date ? '#F97316' : '#94a3b8'
                            }}>
                              {accompanimentData.oral_blanc?.date ? new Date(accompanimentData.oral_blanc.date).toLocaleDateString('fr-FR') : 'À planifier'}
                            </span>
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                  {/* Course Progress Table */}
                  <div className="card" style={{ marginBottom: '2.5rem', padding: '1.5rem' }} data-testid="course-progress-section">
                    <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <BookOpen size={20} /> Avancement des cours
                    </h3>
                    {programCourses.length === 0 ? (
                      <p className="body-medium" style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>
                        Aucun cours disponible pour ce diplôme.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {programCourses.map(course => {
                          const timeEntry = programProgress.find(p => p.courseId === course.id);
                          const timeSecs = timeEntry?.timeSpent || 0;
                          const timeMins = Math.floor(timeSecs / 60);
                          const localProgress = course.presentationType ? userProgressData[course.presentationType] : null;

                          let progressPct = 0;
                          let progressLabel = 'Non commencé';
                          let progressColor = '#94a3b8';
                          let quizInfo = null;

                          if (course.type === 'presentation' && localProgress) {
                            if (localProgress.totalSlides > 0) {
                              progressPct = Math.min(100, Math.round((localProgress.viewedSlides.length / localProgress.totalSlides) * 100));
                            }
                            if (progressPct === 100) {
                              progressLabel = 'Terminé';
                              progressColor = '#16a34a';
                            } else if (progressPct > 0) {
                              progressLabel = `${progressPct}% consulté`;
                              progressColor = '#3B82F6';
                            }
                          } else if (course.type === 'quiz') {
                            if (localProgress?.quizCompleted) {
                              const score = localProgress.quizScore || 0;
                              const total = localProgress.quizTotalQuestions || 30;
                              const pctScore = Math.round((score / total) * 100);
                              progressPct = 100;
                              progressLabel = `${score}/${total} (${pctScore}%)`;
                              progressColor = pctScore >= 70 ? '#16a34a' : '#d97706';
                              quizInfo = { score, total, pctScore };
                            }
                          }

                          return (
                            <div key={course.id} style={{ padding: '1rem', borderRadius: '10px', background: 'var(--bg-section)', border: '1px solid var(--border-light)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                    {course.type === 'quiz' ? <FileText size={14} style={{ color: '#d97706', flexShrink: 0 }} /> : <Presentation size={14} style={{ color: '#3B82F6', flexShrink: 0 }} />}
                                    <p className="body-small" style={{ fontWeight: 600, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {course.title}
                                    </p>
                                  </div>
                                  {course.slideCount && <span className="caption" style={{ color: 'var(--text-muted)' }}>{course.slideCount} slides</span>}
                                  {course.questionCount && <span className="caption" style={{ color: 'var(--text-muted)' }}>{course.questionCount} questions</span>}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                                  {timeMins > 0 && (
                                    <span className="caption" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-muted)' }}>
                                      <Clock size={12} /> {timeMins}min
                                    </span>
                                  )}
                                  <span style={{ padding: '0.15rem 0.6rem', borderRadius: '99px', fontSize: '0.7rem', fontWeight: 600, color: progressColor, background: `${progressColor}15`, border: `1px solid ${progressColor}30` }}>
                                    {progressLabel}
                                  </span>
                                </div>
                              </div>
                              {/* Progress bar */}
                              <div style={{ background: 'var(--border-light)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                                <div style={{
                                  width: `${progressPct}%`,
                                  height: '100%',
                                  background: progressColor,
                                  borderRadius: '4px',
                                  transition: 'width 0.5s ease'
                                }} />
                              </div>
                              {quizInfo && (
                                <p className="caption" style={{ marginTop: '0.5rem', color: quizInfo.pctScore >= 70 ? '#16a34a' : '#d97706' }}>
                                  {quizInfo.pctScore >= 70 ? 'Quiz réussi !' : 'Quiz à refaire (minimum 70% requis)'}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Recevabilité Section */}
                  <div className="card" style={{ marginBottom: '2.5rem', padding: '1.5rem' }} data-testid="recevabilite-section">
                    <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <ClipboardCheck size={20} /> Recevabilité — {enrolledProgram?.title}
                    </h3>

                    {/* File Upload Zone */}
                    {(() => {
                      const existing = programRecevabilite.find(r => r.file);
                      const handleFile = async (file) => {
                        if (!file) return;
                        const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
                        if (!allowed.includes(file.type)) {
                          toast.error('Format non supporté. Utilisez JPG, PNG ou PDF.');
                          return;
                        }
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('Fichier trop volumineux (max 10 Mo)');
                          return;
                        }
                        setUploading(true);
                        try {
                          await uploadRecevabiliteFile(user.id, selectedProgramId, file);
                          toast.success('Fichier de recevabilité envoyé avec succès !');
                          fetchRecevabilite(user.id).then(setRecevabiliteList).catch(() => {});
                        } catch (err) {
                          toast.error(err.message || 'Erreur lors de l\'envoi');
                        } finally {
                          setUploading(false);
                          setDragOver(false);
                        }
                      };

                      return (
                        <div style={{ marginBottom: '1.5rem' }}>
                          {existing?.file ? (
                            <div style={{ padding: '1.25rem', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: 44, height: 44, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: existing.file.content_type === 'application/pdf' ? '#fef2f2' : '#eff6ff' }}>
                                  <FileIcon size={22} style={{ color: existing.file.content_type === 'application/pdf' ? '#dc2626' : '#3B82F6' }} />
                                </div>
                                <div>
                                  <p className="body-small" style={{ fontWeight: 600, margin: 0 }}>{existing.file.original_filename}</p>
                                  <span className="caption" style={{ color: 'var(--text-muted)' }}>{(existing.file.size / 1024).toFixed(0)} Ko</span>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <a
                                  href={getFileUrl(existing.file.storage_path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn-secondary"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                  data-testid="view-recevabilite-file"
                                >
                                  <Download size={14} /> Voir
                                </a>
                                <label
                                  className="btn-secondary"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                >
                                  <Upload size={14} /> Remplacer
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFile(e.target.files[0])}
                                  />
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div
                              data-testid="recevabilite-dropzone"
                              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                              onDragLeave={() => setDragOver(false)}
                              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                              style={{
                                padding: '2rem',
                                borderRadius: '12px',
                                border: `2px dashed ${dragOver ? 'var(--primary)' : '#cbd5e1'}`,
                                background: dragOver ? 'rgba(99, 102, 241, 0.05)' : '#f8fafc',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => document.getElementById('recevabilite-file-input').click()}
                            >
                              <input
                                id="recevabilite-file-input"
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFile(e.target.files[0])}
                              />
                              {uploading ? (
                                <div>
                                  <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
                                  <p className="body-small" style={{ color: 'var(--primary)', fontWeight: 600 }}>Envoi en cours...</p>
                                </div>
                              ) : (
                                <div>
                                  <Upload size={36} style={{ color: '#94a3b8', marginBottom: '0.75rem' }} />
                                  <p className="body-small" style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                                    Glissez votre fichier de recevabilité ici
                                  </p>
                                  <p className="caption" style={{ color: 'var(--text-muted)' }}>
                                    ou cliquez pour sélectionner — JPG, PNG ou PDF (max 10 Mo)
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Status display */}
                    {programRecevabilite.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--bg-section)', borderRadius: '12px' }}>
                        <p className="caption" style={{ color: 'var(--text-muted)' }}>
                          Déposez votre notification de recevabilité ci-dessus.
                        </p>
                      </div>
                    )}
                    {programRecevabilite.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {programRecevabilite.map((r, idx) => {
                          const statusColors = {
                            'recevable': { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a', label: 'Recevable' },
                            'non_recevable': { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', label: 'Non recevable' },
                            'en_attente': { bg: '#fffbeb', border: '#fde68a', text: '#d97706', label: 'En attente' },
                            'envoye': { bg: '#eff6ff', border: '#bfdbfe', text: '#2563eb', label: 'Dossier envoyé' }
                          };
                          const sc = statusColors[r.status] || statusColors['en_attente'];
                          return (
                            <div key={idx} style={{ padding: '1.25rem', borderRadius: '12px', background: sc.bg, border: `1px solid ${sc.border}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <div>
                                  {r.reference && <span className="body-small" style={{ fontWeight: 600 }}>Réf: {r.reference}</span>}
                                </div>
                                <span style={{ padding: '0.25rem 0.75rem', borderRadius: '99px', fontSize: '0.75rem', fontWeight: 600, color: sc.text, background: 'white', border: `1px solid ${sc.border}` }}>
                                  {sc.label}
                                </span>
                              </div>
                              {r.date && <p className="caption" style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>Date : {new Date(r.date).toLocaleDateString('fr-FR')}</p>}
                              {r.notes && <p className="body-small" style={{ marginTop: '0.5rem' }}>{r.notes}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Livret 2 Corrections Section */}
                  <div className="card" style={{ padding: '1.5rem' }} data-testid="livret2-section">
                    <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileEdit size={20} /> Corrections du Livret 2 — {enrolledProgram?.title}
                    </h3>
                    {programCorrections.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--bg-section)', borderRadius: '12px' }}>
                        <FileEdit size={40} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
                        <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          Aucune correction enregistrée pour {enrolledProgram?.title}
                        </p>
                        <p className="caption" style={{ color: 'var(--text-muted)' }}>
                          Les corrections apparaîtront ici au fur et à mesure de votre accompagnement.
                        </p>
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Date</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Bloc</th>
                              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Version</th>
                              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Statut</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Commentaires</th>
                            </tr>
                          </thead>
                          <tbody>
                            {programCorrections.map((c, idx) => {
                              const statusMap = {
                                'en_attente': { label: 'En attente', color: '#d97706', bg: '#fffbeb' },
                                'corrige': { label: 'Corrigé', color: '#2563eb', bg: '#eff6ff' },
                                'a_revoir': { label: 'À revoir', color: '#dc2626', bg: '#fef2f2' },
                                'valide': { label: 'Validé', color: '#16a34a', bg: '#f0fdf4' }
                              };
                              const st = statusMap[c.status] || statusMap['en_attente'];
                              return (
                                <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                                  <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{c.date ? new Date(c.date).toLocaleDateString('fr-FR') : '—'}</td>
                                  <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{c.bloc || '—'}</td>
                                  <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600 }}>V{c.version}</td>
                                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '99px', fontSize: '0.75rem', fontWeight: 600, color: st.color, background: st.bg }}>
                                      {st.label}
                                    </span>
                                  </td>
                                  <td style={{ padding: '0.75rem', fontSize: '0.85rem', maxWidth: '250px' }}>{c.comments || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

  );
};

export default MonParcoursTab;
